import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileText,
  Heart,
  Package,
  Settings as SettingsIcon,
  ShoppingCart,
  Sparkles,
  Store,
  UserSquare2,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/AuthProvider';
import { useSidebarUI } from './sidebarUI';

type TutorialStep = {
  id: string;
  title: string;
  icon: LucideIcon;
  body: string;
};

const STEPS: TutorialStep[] = [
  { id: 'stores', title: 'Stores — start here', icon: Store, body: 'Begin by creating a store. A store is the physical or virtual location where your business operates. Every employee, item and sale belongs to a store, so set this up first.' },
  { id: 'employees', title: 'Employees', icon: Users, body: 'Add the people who will work in each store. Assign them a role (manager or employee) so they get the right access to POS, inventory and reports.' },
  { id: 'inventory', title: 'Inventory', icon: Package, body: 'Open the Inventory hub to add the items you sell — name, SKU, price, stock and category. Inventory drives both quotations and POS, so keep it accurate.' },
  { id: 'quotations', title: 'Quotations', icon: FileText, body: "Quotations let you estimate the price of a customer's planned purchase. Build a draft, share it with the customer, and convert it to a sale when they're ready." },
  { id: 'pos', title: 'Point of Sale (POS)', icon: ShoppingCart, body: 'Use POS to ring up sales. Pick items, take payment, and finish the order — inventory is updated automatically the moment the sale completes.' },
  { id: 'crm', title: 'Customer Relationship Management', icon: UserSquare2, body: "CRM keeps every customer's details and full order history in one place, so you can follow up, send offers and grow repeat business." },
  { id: 'settings', title: 'Settings', icon: SettingsIcon, body: 'Settings is where you configure your business — name, logo, theme, GST, tax, document numbering and contact details. Visit it whenever something changes.' },
];

type Rect = { top: number; left: number; width: number; height: number };

// Scope querySelector to the correct sidebar so we never measure a CSS-hidden element
function findAnchor(id: string, isMobile: boolean): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const scope = isMobile
    ? document.querySelector<HTMLElement>('.mobile-drawer')
    : document.querySelector<HTMLElement>('.desktop-sidebar');
  const container = scope ?? document;
  return container.querySelector<HTMLElement>(`[data-tutorial-id="${id}"]`);
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// Reactively measures a nav anchor element
function useAnchorRect(
  anchorId: string | null,
  enabled: boolean,
  isMobile: boolean,
): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    setRect(null);

    if (!enabled || !anchorId) return;

    const run = () => {
      const el = findAnchor(anchorId, isMobile);
      if (!el) { setRect(null); return; }

      const measure = () => {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        } else {
          setRect(null);
        }
      };
      measure();

      const ro = new ResizeObserver(measure);
      ro.observe(el);
      ro.observe(document.body);
      window.addEventListener('resize', measure);
      window.addEventListener('scroll', measure, true);

      cleanupRef.current = () => {
        ro.disconnect();
        window.removeEventListener('resize', measure);
        window.removeEventListener('scroll', measure, true);
      };
    };

    // Wait for drawer animation (300ms) + a little buffer
    const t = setTimeout(run, 150);
    return () => {
      clearTimeout(t);
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [anchorId, enabled, isMobile]);

  return rect;
}

type NavigationTutorialProps = {
  startSignal: number;
};

export function NavigationTutorial({ startSignal }: NavigationTutorialProps) {
  const { user, loading, displayName } = useAuth();
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useSidebarUI();
  const isMobile = useIsMobile();

  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [availableSteps, setAvailableSteps] = useState<TutorialStep[]>([]);

  const prevCollapsedRef = useRef<boolean | null>(null);
  const prevMobileOpenRef = useRef<boolean | null>(null);

  const totalPages = availableSteps.length + 1;
  const isThanks = index >= availableSteps.length;
  const currentStep = isThanks ? null : availableSteps[index];

  const rect = useAnchorRect(
    currentStep?.id ?? null,
    active && !isThanks && (isMobile ? mobileOpen : true),
    isMobile,
  );

  const start = useCallback(() => {
    prevCollapsedRef.current = collapsed;
    prevMobileOpenRef.current = mobileOpen;

    if (isMobile) {
      setMobileOpen(true);
    } else {
      if (collapsed) setCollapsed(false);
    }

    // Wait for drawer/sidebar animation before scanning for anchors
    setTimeout(() => {
      const steps = STEPS.filter((s) => findAnchor(s.id, isMobile));
      setAvailableSteps(steps);
      setIndex(0);
      setActive(true);
    }, 400);
  }, [collapsed, setCollapsed, isMobile, mobileOpen, setMobileOpen]);

  const finish = useCallback(() => {
    setActive(false);
    if (!isMobile && prevCollapsedRef.current !== null) setCollapsed(prevCollapsedRef.current);
    if (isMobile && prevMobileOpenRef.current === false) setMobileOpen(false);
    prevCollapsedRef.current = null;
    prevMobileOpenRef.current = null;
  }, [setCollapsed, isMobile, setMobileOpen]);

  useEffect(() => {
    if (!startSignal || loading || !user) return;
    const raf = requestAnimationFrame(() => start());
    return () => cancelAnimationFrame(raf);
  }, [startSignal, loading, user, start]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, totalPages - 1));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, finish, totalPages]);

  function next() { setIndex((i) => Math.min(i + 1, totalPages - 1)); }
  function prev() { setIndex((i) => Math.max(i - 1, 0)); }

  if (!active || typeof document === 'undefined') return null;

  const stepNumber = Math.min(index + 1, totalPages);
  const showCallout = isThanks || isMobile || Boolean(rect);

  return (
    <>
      {/* LAYER 1 — Backdrop z-[100]
          Mobile: only darken the bottom 42% so the drawer nav stays readable.
          Desktop: full-screen dark overlay. */}
      {createPortal(
        <div className="pointer-events-none fixed inset-0 z-[100]">
          {isMobile ? (
            <>
              <div className="absolute inset-x-0 top-0 h-[58%]" aria-hidden />
              <div
                className="pointer-events-auto absolute inset-x-0 bottom-0 h-[42%] bg-black/75"
                onClick={finish}
                aria-hidden
              />
            </>
          ) : (
            <div
              className="pointer-events-auto absolute inset-0 bg-black/60 backdrop-blur-[1px]"
              onClick={finish}
              aria-hidden
            />
          )}
        </div>,
        document.body,
      )}

      {/* LAYER 2 — Highlight ring + step badge z-[200]
          Separate portal so it paints above the mobile drawer stacking context (z-50). */}
      {rect && !isThanks && createPortal(
        <div className="pointer-events-none fixed inset-0 z-[200]">
          {/* Gold ring around the nav item */}
          <div
            className="absolute rounded-md ring-2 ring-gold-500 shadow-[0_0_0_4px_rgba(201,164,65,0.25)] transition-all duration-300"
            style={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
            }}
            aria-hidden
          />
          {/* Step number badge */}
          <div
            className="absolute flex h-7 w-7 items-center justify-center rounded-full bg-gold-500 text-xs font-bold tabular-nums text-ink shadow-lg ring-2 ring-ink transition-all duration-300"
            style={{
              top: rect.top + rect.height / 2 - 14,
              left: isMobile ? rect.left + rect.width - 10 : rect.left - 10,
            }}
            aria-hidden
          >
            {stepNumber}
          </div>
        </div>,
        document.body,
      )}

      {/* LAYER 3 — Callout card z-[300] */}
      {showCallout && createPortal(
        <Callout
          rect={rect}
          isThanks={isThanks}
          isMobile={isMobile}
          stepNumber={stepNumber}
          totalPages={totalPages}
          title={currentStep ? currentStep.title : "You're all set"}
          icon={currentStep ? currentStep.icon : Heart}
          body={
            currentStep
              ? currentStep.body
              : `Thank you${displayName ? `, ${displayName}` : ''}! That's the full workflow — Stores → Employees → Inventory → Quotations → POS → CRM → Settings. You can revisit any module from the sidebar at any time. Welcome to CHHABIGHAR!`
          }
          onPrev={prev}
          onNext={next}
          onFinish={finish}
          canGoBack={index > 0}
        />,
        document.body,
      )}
    </>
  );
}

type CalloutProps = {
  rect: Rect | null;
  isThanks: boolean;
  isMobile: boolean;
  stepNumber: number;
  totalPages: number;
  title: string;
  icon: LucideIcon;
  body: string;
  canGoBack: boolean;
  onPrev: () => void;
  onNext: () => void;
  onFinish: () => void;
};

function Callout({ rect, isThanks, isMobile, stepNumber, totalPages, title, icon: Icon, body, canGoBack, onPrev, onNext, onFinish }: CalloutProps) {
  const GAP = 12;
  const rightEdgePad = 16;

  let style: React.CSSProperties;
  let showLeftArrow = false;

  if (isMobile && !isThanks) {
    // Pinned to bottom zone — never overlaps the nav items above
    style = {
      position: 'fixed',
      bottom: '1rem',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'min(22rem, calc(100vw - 2rem))',
      zIndex: 300,
    };
  } else if (!isMobile && rect && !isThanks) {
    // Anchored to the right of the highlighted nav item
    const anchoredMaxW = `calc(100vw - ${rect.left + rect.width + GAP + rightEdgePad}px)`;
    style = {
      position: 'fixed',
      top: Math.max(16, rect.top + rect.height / 2 - 80),
      left: rect.left + rect.width + GAP,
      maxWidth: anchoredMaxW,
      width: `min(40rem, ${anchoredMaxW})`,
      zIndex: 300,
    };
    showLeftArrow = true;
  } else {
    // Centered fallback (thanks page or no rect)
    style = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(44rem, calc(100vw - 2rem))',
      zIndex: 300,
    };
  }

  return (
    <div
      className="rounded-xl border border-gold-500/40 bg-background p-4 shadow-2xl"
      style={style}
      role="dialog"
      aria-modal="true"
      aria-labelledby="nav-tutorial-title"
    >
      {showLeftArrow && (
        <span className="absolute -left-1.5 top-8 h-3 w-3 rotate-45 border-b border-l border-gold-500/40 bg-background" aria-hidden />
      )}

      {/* Header */}
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-gold-500" />
        <span className="shrink-0">Quick tour</span>
        <span className="ml-auto shrink-0">Step {stepNumber} of {totalPages}</span>
        <button
          type="button"
          onClick={onFinish}
          className="ml-2 shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Close tour"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Icon + content */}
      <div className="mt-3 flex items-start gap-3">
        <span className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gold-500/15 text-gold-600',
          isThanks && 'bg-gold-500 text-ink',
        )}>
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 id="nav-tutorial-title" className="font-serif text-base font-semibold leading-tight tracking-tight">
            {title}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
        </div>
      </div>

      {/* Progress dots */}
      <ol className="mt-3 flex flex-wrap gap-1.5" aria-label="Tutorial progress">
        {Array.from({ length: totalPages }).map((_, i) => (
          <li key={i}>
            <span
              className={cn(
                'block h-1.5 w-5 rounded-full transition-colors sm:w-7',
                i === stepNumber - 1 ? 'bg-gold-500' : i < stepNumber - 1 ? 'bg-gold-500/60' : 'bg-muted',
              )}
              aria-hidden
            />
          </li>
        ))}
      </ol>

      {/* Actions */}
      <div className="mt-3 flex items-center justify-between gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onFinish} className="text-muted-foreground hover:text-foreground">
          Skip
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onPrev} disabled={!canGoBack}>
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </Button>
          {isThanks ? (
            <Button type="button" variant="gold" size="sm" onClick={onFinish}>
              <CheckCircle2 className="h-4 w-4" /> Done
            </Button>
          ) : (
            <Button type="button" variant="gold" size="sm" onClick={onNext}>
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
